// src/components/GeneradorEventos.js
// Componente para generar entrenamientos automáticamente desde una serie de entrenamiento

import React, { useState } from 'react';
import { CheckCircle2, Loader2, PlayCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { generarEventosDeSerie } from '../lib/supabase';
import { parseLocalDate, getMexicoDateString } from '../lib/dateUtils';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

/**
 * GeneradorEventos - Modal para generar entrenamientos automáticamente desde una serie
 *
 * @param {Object} props
 * @param {Object} props.serie - Serie de entrenamiento desde la cual generar
 * @param {boolean} props.isOpen - Estado del modal
 * @param {Function} props.onClose - Callback al cerrar
 * @param {Function} props.onSuccess - Callback después de generar con éxito
 */
const GeneradorEventos = ({ serie, isOpen, onClose, onSuccess }) => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);

  // Resetear estado cuando se cierra el modal
  const handleClose = () => {
    setFechaInicio('');
    setFechaFin('');
    setResultado(null);
    setError(null);
    onClose();
  };

  const guard = useUnsavedGuard({ onClose: handleClose });

  // Inicializar fechas con valores predeterminados al abrir
  React.useEffect(() => {
    if (isOpen && !fechaInicio) {
      // Fecha inicio: hoy o la fecha_inicio de la serie
      const hoy = new Date();
      const inicioSerie = serie?.fecha_inicio ? parseLocalDate(serie.fecha_inicio) : null;
      const inicio = inicioSerie && inicioSerie > hoy ? inicioSerie : hoy;

      // Fecha fin: un mes después
      const fin = new Date(inicio);
      fin.setMonth(fin.getMonth() + 1);

      setFechaInicio(getMexicoDateString(inicio));
      setFechaFin(getMexicoDateString(fin));
    }
  }, [isOpen, serie, fechaInicio]);

  const handleGenerar = async () => {
    setError(null);
    setResultado(null);

    // Validaciones
    if (!fechaInicio || !fechaFin) {
      setError('Por favor selecciona ambas fechas');
      return;
    }

    const inicio = parseLocalDate(fechaInicio);
    const fin = parseLocalDate(fechaFin);

    if (inicio > fin) {
      setError('La fecha de inicio debe ser anterior a la fecha fin');
      return;
    }

    // Validar que no sea un rango muy largo (más de 6 meses)
    const mesesDiferencia = (fin.getFullYear() - inicio.getFullYear()) * 12 +
                            (fin.getMonth() - inicio.getMonth());

    if (mesesDiferencia > 6) {
      setError('El rango no puede ser mayor a 6 meses. Por favor genera en bloques más pequeños.');
      return;
    }

    try {
      setGenerando(true);

      const { data, error: genError } = await generarEventosDeSerie(
        serie.id,
        fechaInicio,
        fechaFin
      );

      if (genError) throw genError;

      setResultado(data);
      guard.resetDirty();

      // Llamar a onSuccess después de un momento para que el usuario vea el resultado
      if (onSuccess) {
        setTimeout(() => {
          onSuccess(data);
        }, 2000);
      }
    } catch (err) {
      console.error('Error generando eventos:', err);
      setError(err.message || 'Error al generar eventos');
    } finally {
      setGenerando(false);
    }
  };

  if (!serie) return null;

  // Formatear días de la semana
  const diasFormateados = serie.dias_semana?.map(dia => {
    const nombresDias = {
      'lunes': 'Lun',
      'martes': 'Mar',
      'miercoles': 'Mié',
      'jueves': 'Jue',
      'viernes': 'Vie',
      'sabado': 'Sáb',
      'domingo': 'Dom'
    };
    return nombresDias[dia] || dia;
  }).join(', ');

  return (
    <Dialog open={isOpen} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <PlayCircle className="h-5 w-5 text-primary" />
            <span>Generar entrenamientos en la agenda</span>
          </DialogTitle>
          <DialogDescription>
            Crea los entrenamientos de este horario en el rango de fechas elegido. Aparecerán en la agenda y en la pantalla Hoy.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Información de la serie */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Horario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="font-medium">Nombre:</span> {serie.nombre}
              </div>
              <div>
                <span className="font-medium">Días:</span> {diasFormateados}
              </div>
              <div>
                <span className="font-medium">Horario:</span> {serie.hora_inicio?.slice(0, 5)} - {serie.hora_fin?.slice(0, 5)}
              </div>
              {serie.grupos && (
                <div>
                  <span className="font-medium">Grupo:</span> {serie.grupos.nombre_del_grupo}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selección de rango de fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha-inicio">Fecha inicio</Label>
              <Input
                id="fecha-inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                disabled={generando}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha-fin">Fecha fin</Label>
              <Input
                id="fecha-fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                disabled={generando}
              />
            </div>
          </div>

          {/* Mensaje de error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Resultado de la generación */}
          {resultado && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">¡Entrenamientos generados!</div>
                <div className="mt-1 text-sm">
                  • {resultado.eventos_creados} entrenamientos nuevos creados
                  {resultado.eventos_existentes > 0 && (
                    <div>• {resultado.eventos_existentes} ya existían</div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => guard.onOpenChange(false)}
            disabled={generando}
          >
            {resultado ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!resultado && (
            <Button
              onClick={handleGenerar}
              disabled={generando || !fechaInicio || !fechaFin}
            >
              {generando ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Generar entrenamientos
                </>
              )}
            </Button>
          )}
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
};

export default GeneradorEventos;



